import { ListOddIcon } from '@tableau/icons/ListOddIcon';
import { ThumbnailOddIcon } from '@tableau/icons/ThumbnailOddIcon';
import { List, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Telemetry } from '~commonInfra/services/Telemetry';
import { CreateExplorerEvent } from '~commonInfra/telemetryEvents/TelemetryEventCreator';
import { ExplorerDisplayMode, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { TallListItemCheckmarkCentered } from '~src/commonInfra/styles/CommonStyles';

import { Messages } from '../../../codegen/Localize';
import { DisplayMode } from '../../../sessionManagement/serverApi/types/ISessionInfo';
import { ExplorerStyles } from './ExplorerStyles';
import { ExplorerToolbarTestIds } from './ExplorerToolbar';
import { ExplorerDropdownStyles } from './widgets/ExplorerDropdownStyles';

export namespace ExplorerViewModeSwitcherTestIds {
  export const CardButton = 'view-mode-toggle-thumbnail';
  export const ListButton = 'view-mode-toggle-list';
  export const MainButton = 'view-mode-toggle';
}

// Visible "View As:" label (WCAG SC 3.3.2). A single-id aria-labelledby lets SingleSelectDropdown
// auto-append the button's own id and value id, so screen readers announce the label plus the selected
// mode, e.g. "View As: Grid View". (A space-delimited multi-id aria-labelledby is used verbatim by the
// widget and would drop the selected value from the accessible name — matches SortByDropdown pattern.)
export const VIEW_MODE_SWITCHER_LABEL_ID = 'view-mode-switcher-label';
export const VIEW_MODE_SWITCHER_BUTTON_ID = 'view-mode-switcher-button';

interface ViewModeInfo {
  mode: DisplayMode;
  icon: typeof ThumbnailOddIcon;
  testId: string;
  displayValue: () => string;
}

const ViewModeInfos: ViewModeInfo[] = [
  {
    mode: 'thumbnail',
    icon: ThumbnailOddIcon,
    testId: ExplorerViewModeSwitcherTestIds.CardButton,
    displayValue: Messages.explorerViewMode_thumbnail
  },
  {
    mode: 'list',
    icon: ListOddIcon,
    testId: ExplorerViewModeSwitcherTestIds.ListButton,
    displayValue: Messages.explorerViewMode_list
  }
];

export interface ExplorerViewModeSwitcherProps {
  readonly currentViewMode: DisplayMode;
  readonly changeViewMode: (viewMode: DisplayMode) => void;
  readonly disabled: boolean;
}

export class ExplorerViewModeSwitcher extends React.PureComponent<ExplorerViewModeSwitcherProps> {

  private handleChangeViewMode = (mode: DisplayMode): void => {
    this.props.changeViewMode(mode);

    Telemetry.sendEvent<ExplorerDisplayMode>(CreateExplorerEvent<ExplorerDisplayMode>(
      {
        type: TelemetryEvents.ExplorerDisplayMode,
        previousMode: this.props.currentViewMode,
        newMode: mode
      }
    ));
  };

  private renderModeIcon = (mode: ViewModeInfo, alt?: string) => <mode.icon
    alt={alt}
    aria-hidden={alt ? undefined : true}
    className={ExplorerStyles.ExplorerViewModeMainButtonImg}
    size={ExplorerStyles.buttonIconSize}
  />;

  public render(): JSX.Element {
    const currentMode: ViewModeInfo | undefined = _.find(ViewModeInfos, (info) => info.mode === this.props.currentViewMode); // can be undefined in tests
    return (
      <>
        <span id={VIEW_MODE_SWITCHER_LABEL_ID} className={ExplorerStyles.toolbarDropdownLabelClass(this.props.disabled)}>
          {Messages.explorerViewMode()}
        </span>
        <SingleSelectDropdown
          id={VIEW_MODE_SWITCHER_BUTTON_ID}
          aria-label={null}
          aria-labelledby={VIEW_MODE_SWITCHER_LABEL_ID}
          children={currentMode && this.renderModeIcon(currentMode, currentMode?.displayValue())}
          disabled={this.props.disabled}
          label=''
          list={<List
            aria-label={null}
            aria-labelledby={VIEW_MODE_SWITCHER_LABEL_ID}
            className={TallListItemCheckmarkCentered}
            items={ViewModeInfos.map<ListItem>((info: ViewModeInfo) => ({
              value: info.mode,
              content: <div className={ExplorerDropdownStyles.ExplorerDropdownListItemContainer}>
                {info.displayValue()}
                {this.renderModeIcon(info)}
              </div>,
              testId: info.testId + '-MenuItem', // not actually a menuitem, but this keeps backwards-compatibility with tests
            }))}
            style={{ minWidth: 150 }}
          />}
          onChange={this.handleChangeViewMode}
          testId={ExplorerToolbarTestIds.ViewModeSwitcher}
          title={Messages.explorerViewMode()}
          value={this.props.currentViewMode}
        />
      </>
    );
  }
}
